import React from "react";
import { Box, Stack, Typography, Chip, IconButton } from "@mui/material";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

interface Video {
    _id: string;
    title: string;
    duration: string;
    thumbnail: string;
    isVisible: boolean;
    isTrial: boolean;
}

interface VideoListItemProps {
    video: Video;
    onEdit?: (video: Video) => void;
    onDelete?: (video: Video) => void;
}

const VideoListItem: React.FC<VideoListItemProps> = ({
    video,
    onEdit,
    onDelete
}) => {
    return (
        <Box
            key={video._id}
            sx={{
                display: "flex",
                alignItems: "center",
                p: 1,
                borderBottom: "1px solid #f0f0f0",
            }}
        >
            {/* LEFT: icon + thumbnail + title */}
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ flex: 2.5, minWidth: 0 }}
            >
                <VideoLibraryOutlinedIcon
                    sx={{ fontSize: 22, color: "#A8A8A8", flexShrink: 0 }}
                />
                <Box
                    component="img"
                    src={video.thumbnail}
                    alt={video.title}
                    sx={{
                        width: 60,
                        height: 40,
                        borderRadius: 1,
                        objectFit: "cover",
                        flexShrink: 0,
                    }}
                />
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                        {video.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {video.duration}
                    </Typography>
                </Box>
            </Stack>

            {/* MIDDLE: Display + Trial */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 8,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ color: "#818181", display: "flex", alignItems: "center", gap: 1 }}
                >
                    Display:
                    <Chip
                        size="small"
                        label={video.isVisible ? "visible" : "hidden"}
                        sx={{
                            backgroundColor: video.isVisible ? "#D7F2E7" : "#D8D8DD",
                            color: video.isVisible ? "#028A4F" : "#818181",
                        }}
                        variant="filled"
                    />
                </Typography>

                <Typography
                    variant="caption"
                    sx={{ color: "#818181", display: "flex", alignItems: "center", gap: 1 }}
                >
                    Trial:
                    <Chip
                        size="small"
                        label={video.isTrial ? "Yes" : "No"}
                        sx={{
                            backgroundColor: video.isTrial ? "#FA3D3D33" : "#E5F8FE",
                            color: video.isTrial ? "#FA3D3D" : "#26C6F9",
                        }}
                        variant="filled"
                    />
                </Typography>
            </Box>

            {/* RIGHT: actions */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                {onEdit && (
                    <IconButton size="small" onClick={() => onEdit(video)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                )}
                {onDelete && (
                    <IconButton size="small" onClick={() => onDelete(video)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};

export default VideoListItem;
